import { PlayerRef } from '@remotion/player';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useVideoConfig } from 'remotion';
import { EditModeContext } from '../edit-mode';
import { BlurItem } from '../items/blur/blur-item-type';
import { SolidItem } from '../items/solid/solid-item-type';
import { PreviewSizeContext } from '../preview-size';
import { addItem } from '../state/actions/add-item';
import { changeItem } from '../state/actions/change-item';
import { deleteItems } from '../state/actions/delete-items';
import { calculateScale } from '../utils/calculate-canvas-transformation';
import { generateRandomId } from '../utils/generate-random-id';
import { useWriteContext } from '../utils/use-context';
import { CanvasSizeContext } from './canvas-size';

type DrawableItem = BlurItem | SolidItem;

type DrawingItemConfig = Omit<
  DrawableItem,
  'id' | 'from' | 'top' | 'left' | 'width' | 'height' | 'isDraggingInTimeline'
>;

interface UseDrawingToolOptions {
  playerRef: React.RefObject<PlayerRef | null>;
  createItemConfig: () => DrawingItemConfig;
}

interface ItemBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

const calculateItemBounds = (
  startLeft: number,
  startTop: number,
  currentWidth: number,
  currentHeight: number,
): ItemBounds => {
  let finalLeft = startLeft;
  let finalTop = startTop;
  let finalWidth = currentWidth;
  let finalHeight = currentHeight;

  if (finalWidth < 0) {
    finalLeft = startLeft + finalWidth;
    finalWidth = Math.abs(finalWidth);
  } else if (finalWidth === 0) {
    finalWidth = 1;
  }

  if (finalHeight < 0) {
    finalTop = startTop + finalHeight;
    finalHeight = Math.abs(finalHeight);
  } else if (finalHeight === 0) {
    finalHeight = 1;
  }

  return {
    left: Math.round(finalLeft),
    top: Math.round(finalTop),
    width: Math.abs(finalWidth),
    height: Math.abs(finalHeight),
  };
};

const checkItemOverlapsCanvas = (
  item: ItemBounds,
  canvasWidth: number,
  canvasHeight: number,
): boolean => {
  return (
    item.left + item.width > 0 &&
    item.left < canvasWidth &&
    item.top + item.height > 0 &&
    item.top < canvasHeight
  );
};

export const useDrawingTool = ({
  playerRef,
  createItemConfig,
}: UseDrawingToolOptions) => {
  const { setState } = useWriteContext();
  const { width: canvasWidth, height: canvasHeight } = useVideoConfig();
  const { size } = useContext(PreviewSizeContext);
  const { setEditMode } = useContext(EditModeContext);
  const canvasSize = useContext(CanvasSizeContext);
  const isDrawingRef = useRef(false);

  const getContainerRect = useCallback(() => {
    return playerRef.current?.getContainerNode()?.getBoundingClientRect();
  }, [playerRef]);

  const isInsideCanvas = useCallback(
    (clientX: number, clientY: number): boolean => {
      const rect = getContainerRect();
      if (!rect) return false;
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    },
    [getContainerRect],
  );

  const startDrawing = useCallback(
    (clientX: number, clientY: number) => {
      if (isDrawingRef.current || !canvasSize) {
        return;
      }

      const containerRect = getContainerRect();
      if (!containerRect) {
        return;
      }

      isDrawingRef.current = true;

      const scale = calculateScale({
        canvasSize,
        compositionWidth: canvasWidth,
        compositionHeight: canvasHeight,
        previewSize: size.size,
      });

      const xOnCanvas = (clientX - containerRect.left) / scale;
      const yOnCanvas = (clientY - containerRect.top) / scale;
      const id = generateRandomId();
      const startLeft = xOnCanvas;
      const startTop = yOnCanvas;

      setState({
        update: (state) =>
          addItem({
            state,
            item: {
              ...createItemConfig(),
              id,
              from: playerRef.current?.getCurrentFrame() ?? 0,
              top: Math.round(startTop),
              left: Math.round(startLeft),
              width: 100,
              height: 100,
              isDraggingInTimeline: false,
            } as DrawableItem,
            select: true,
            position: { type: 'front' },
          }),
        commitToUndoStack: false,
      });

      const onPointerMove = (evt: PointerEvent) => {
        const rect = getContainerRect();
        if (!rect) return;

        const currentWidth = Math.round(
          (evt.clientX - rect.left) / scale - xOnCanvas,
        );
        const currentHeight = Math.round(
          (evt.clientY - rect.top) / scale - yOnCanvas,
        );

        const bounds = calculateItemBounds(
          startLeft,
          startTop,
          currentWidth,
          currentHeight,
        );

        setState({
          update: (state) =>
            changeItem(state, id, (item) => ({
              ...item,
              ...bounds,
            })),
          commitToUndoStack: false,
        });
      };

      const onPointerUp = (evt: PointerEvent) => {
        const rect = getContainerRect();
        const endedInsideCanvas = isInsideCanvas(evt.clientX, evt.clientY);

        let shouldKeepItem = endedInsideCanvas;

        if (!shouldKeepItem && rect) {
          const currentWidth = Math.round(
            (evt.clientX - rect.left) / scale - xOnCanvas,
          );
          const currentHeight = Math.round(
            (evt.clientY - rect.top) / scale - yOnCanvas,
          );
          const bounds = calculateItemBounds(
            startLeft,
            startTop,
            currentWidth,
            currentHeight,
          );
          shouldKeepItem = checkItemOverlapsCanvas(
            bounds,
            canvasWidth,
            canvasHeight,
          );
        }

        if (shouldKeepItem) {
          setState({
            update: (state) => state,
            commitToUndoStack: true,
          });
        } else {
          setState({
            update: (state) => deleteItems(state, [id]),
            commitToUndoStack: false,
          });
        }

        setEditMode('select');
        isDrawingRef.current = false;

        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [
      canvasSize,
      canvasWidth,
      canvasHeight,
      createItemConfig,
      getContainerRect,
      isInsideCanvas,
      playerRef,
      setEditMode,
      setState,
      size.size,
    ],
  );

  useEffect(() => {
    const onWindowPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      startDrawing(event.clientX, event.clientY);
    };

    window.addEventListener('pointerdown', onWindowPointerDown);
    return () => {
      window.removeEventListener('pointerdown', onWindowPointerDown);
    };
  }, [startDrawing]);
};
